import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Edit, Search, ChevronLeft, ChevronRight, ChevronDown, User, ArrowUpDown, ArrowUp, ArrowDown, Eye,
  X, RefreshCw, Layers, Zap, Smartphone, Monitor, Bookmark, ShoppingCart, ShoppingBag,
  ScanBarcode, Heart, Package, Store, ExternalLink, Clock, ChevronRight as ChevronRightIcon, Bell, Send,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';
import {
  fetchUsers, fetchUsersCount, fetchCountriesForDropdown, fetchStatesByCountry,
  fetchUserTimeline, fetchUserBookmarks, fetchUserCart, fetchUserBarcodeScans, fetchUserSearches, fetchUserProductViews,
  fetchUserFcmTokens, sendUserNotification,
  UserTimeline, TimelineSession, UserBookmarkItem, UserCartItem, UserBarcodeScan, UserSearchKeyword, UserProductView,
} from '../services/api.ts';
import './Users.css';
import './UserActivity.css';

interface UserData {
  id: string;
  user_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone: string;
  postcode: number;
  state: string;
  country: string;
  country_id: number;
  device_name: string;
  is_active: boolean;
  ads: boolean;
  created_at: string;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

interface State {
  id: number;
  name: string;
  country_id: number;
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterTestAccount, setFilterTestAccount] = useState<'all' | 'real' | 'test'>('real');
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [filterState, setFilterState] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState<'email' | 'state' | 'postcode' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Activity modal state
  const [activityUser, setActivityUser] = useState<UserData | null>(null);
  const [timeline, setTimeline] = useState<UserTimeline | null>(null);
  const [tlLoading, setTlLoading] = useState(false);
  type ModalTab = 'activity' | 'bookmarks' | 'cart' | 'barcodes' | 'searches' | 'product_views' | 'notifications';
  const [activeTab, setActiveTab] = useState<ModalTab>('activity');
  const [bookmarks, setBookmarks] = useState<UserBookmarkItem[]>([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [cart, setCart] = useState<UserCartItem[]>([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [barcodeScans, setBarcodeScans] = useState<UserBarcodeScan[]>([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [searches, setSearches] = useState<UserSearchKeyword[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [productViews, setProductViews] = useState<UserProductView[]>([]);
  const [productViewsLoading, setProductViewsLoading] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  // Notification tab state
  const [notifEnabled, setNotifEnabled] = useState<boolean | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState<string | null>(null);

  const handleSendNotification = async () => {
    if (!activityUser || !notifTitle.trim() || !notifBody.trim()) return;
    setNotifSending(true);
    setNotifResult(null);
    try {
      await sendUserNotification(activityUser.user_id, notifTitle.trim(), notifBody.trim());
      setNotifResult('success');
      setNotifTitle('');
      setNotifBody('');
    } catch {
      setNotifResult('error');
    } finally {
      setNotifSending(false);
    }
  };

  const openActivity = async (user: UserData) => {
    setActivityUser(user);
    setActiveTab('activity');
    setTlLoading(true);
    setTimeline(null);
    setBookmarks([]);
    setCart([]);
    setBarcodeScans([]);
    setSearches([]);
    setProductViews([]);
    setNotifEnabled(null);
    setNotifTitle('');
    setNotifBody('');
    setNotifResult(null);
    try {
      const data = await fetchUserTimeline(user.user_id, 30);
      setTimeline(data);
    } catch {
      setTimeline(null);
    } finally {
      setTlLoading(false);
    }
  };

  const switchTab = async (tab: ModalTab) => {
    if (!activityUser) return;
    setActiveTab(tab);
    const uid = activityUser.user_id;
    if (tab === 'bookmarks' && bookmarks.length === 0 && !bookmarksLoading) {
      setBookmarksLoading(true);
      try { setBookmarks(await fetchUserBookmarks(uid)); } catch { setBookmarks([]); } finally { setBookmarksLoading(false); }
    }
    if (tab === 'cart' && cart.length === 0 && !cartLoading) {
      setCartLoading(true);
      try { setCart(await fetchUserCart(uid)); } catch { setCart([]); } finally { setCartLoading(false); }
    }
    if (tab === 'barcodes' && barcodeScans.length === 0 && !barcodesLoading) {
      setBarcodesLoading(true);
      try { setBarcodeScans(await fetchUserBarcodeScans(uid, 30)); } catch { setBarcodeScans([]); } finally { setBarcodesLoading(false); }
    }
    if (tab === 'searches' && searches.length === 0 && !searchesLoading) {
      setSearchesLoading(true);
      try { setSearches(await fetchUserSearches(uid, 30)); } catch { setSearches([]); } finally { setSearchesLoading(false); }
    }
    if (tab === 'product_views' && productViews.length === 0 && !productViewsLoading) {
      setProductViewsLoading(true);
      try { setProductViews(await fetchUserProductViews(uid, 30)); } catch { setProductViews([]); } finally { setProductViewsLoading(false); }
    }
    if (tab === 'notifications' && notifEnabled === null && !notifLoading) {
      setNotifLoading(true);
      setNotifTitle('');
      setNotifBody('');
      setNotifResult(null);
      try {
        const data = await fetchUserFcmTokens(uid);
        setNotifEnabled(data.tokens.length > 0);
      } catch {
        setNotifEnabled(false);
      } finally {
        setNotifLoading(false);
      }
    }
  };

  const closeActivity = () => {
    setActivityUser(null);
    setTimeline(null);
    setBookmarks([]);
    setCart([]);
    setBarcodeScans([]);
    setSearches([]);
    setProductViews([]);
    setActiveTab('activity');
  };

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      if (filterCountry !== undefined) {
        try {
          const data = await fetchStatesByCountry(filterCountry);
          setStates(data);
        } catch (err) {
          console.error('Error loading states:', err);
          setStates([]);
        }
      } else {
        setStates([]);
        setFilterState(undefined);
      }
    };
    loadStates();
  }, [filterCountry]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * usersPerPage;
        const testAccount = filterTestAccount === 'all' ? undefined : filterTestAccount === 'test';
        const [usersData, countData] = await Promise.all([
          fetchUsers(offset, usersPerPage, filterCountry, filterState, testAccount),
          fetchUsersCount(filterCountry, filterState, testAccount)
        ]);
        setUsers(usersData || []);  // Handle null/undefined response
        setTotalUsers(countData || 0);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
        console.error('Error loading users:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [currentPage, filterCountry, filterState, filterTestAccount, usersPerPage]);

  const filteredUsers = (users || []).filter((user) => {
    const fullName = `${user.first_name || ''} ${user.middle_name || ''} ${user.last_name || ''}`.toLowerCase();
    const matchesSearch = 
      fullName.includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || '').includes(searchTerm);
    
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);

    // Country filtering is now done server-side, so no need to filter here
    return matchesSearch && matchesStatus;
  });

  // Sort filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortColumn) return 0;
    let valA: string | number = '';
    let valB: string | number = '';
    if (sortColumn === 'email') {
      valA = (a.email || '').toLowerCase();
      valB = (b.email || '').toLowerCase();
    } else if (sortColumn === 'state') {
      valA = (a.state || '').toLowerCase();
      valB = (b.state || '').toLowerCase();
    } else if (sortColumn === 'postcode') {
      valA = a.postcode || 0;
      valB = b.postcode || 0;
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSort = (column: 'email' | 'state' | 'postcode') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: 'email' | 'state' | 'postcode') => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="sort-icon idle" />;
    return sortDirection === 'asc'
      ? <ArrowUp size={14} className="sort-icon active" />
      : <ArrowDown size={14} className="sort-icon active" />;
  };

  const getDisplayId = (id: string) => {
    const idStr = String(id);
    return idStr.length > 8 ? `${idStr.substring(0, 8)}...` : idStr;
  };

  if (loading) {
    return (
      <div className="users-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h2>Users</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Country:</label>
          <select 
            value={filterCountry ?? ''} 
            onChange={(e) => {
              const value = e.target.value;
              setFilterCountry(value === '' ? undefined : parseInt(value, 10));
              setFilterState(undefined); // Reset state when country changes
              setCurrentPage(1); // Reset to first page when filter changes
            }}
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>State:</label>
          <select
            value={filterState ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilterState(value === '' ? undefined : parseInt(value, 10));
              setCurrentPage(1);
            }}
            disabled={filterCountry === undefined}
          >
            <option value="">All States</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Account:</label>
          <select value={filterTestAccount} onChange={(e) => { setFilterTestAccount(e.target.value as any); setCurrentPage(1); }}>
            <option value="all">All</option>
            <option value="real">Real Users</option>
            <option value="test">Test Accounts</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Rows:</label>
          <select
            value={usersPerPage}
            onChange={(e) => {
              setUsersPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={75}>75</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {sortedUsers.length} of {totalUsers} users
          </span>
        </div>
      </div>

      {sortedUsers.length === 0 ? (
        <div className="empty-state">
          <p>No users found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th className="sortable-th" onClick={() => handleSort('email')}>
                    Email {renderSortIcon('email')}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort('state')}>
                    Location {renderSortIcon('state')}
                  </th>
                  <th>Device Name</th>
                  <th className="sortable-th" onClick={() => handleSort('postcode')}>
                    Postcode {renderSortIcon('postcode')}
                  </th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="id-cell">
                      <div className="user-id-wrapper">
                        <div className="user-avatar-small">
                          <User size={14} />
                        </div>
                        <span title={user.id}>{getDisplayId(user.id)}</span>
                      </div>
                    </td>
                    <td className="name-cell">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="email-cell">
                      {user.email}
                    </td>
                    <td className="location-cell">
                      {user.state ? `${user.state}, ` : ''}{user.country || 'N/A'}
                    </td>
                    <td className="device-cell">
                      <div>{user.device_name || 'N/A'}</div>
                      <div style={{ fontSize: '0.78em', color: '#999' }}>{user.user_id}</div>
                    </td>
                    <td className="postcode-cell">
                      {user.postcode || 'N/A'}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => navigate(`/dashboard/users/edit/${user.user_id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="action-btn view"
                        onClick={() => openActivity(user)}
                        title="View Activity"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Activity Modal */}
      {activityUser && (
        <div className="ua-modal-overlay" onClick={closeActivity}>
          <div className="ua-modal" onClick={e => e.stopPropagation()}>
            <div className="ua-modal-header">
              <div className="ua-modal-title">
                <div className="ua-avatar lg">{(activityUser.first_name || activityUser.user_id || '?')[0].toUpperCase()}</div>
                <div>
                  <h3>{activityUser.first_name} {activityUser.last_name}</h3>
                  <span className="ua-modal-sub">
                    {activityUser.email || activityUser.user_id} · {activityUser.country}
                  </span>
                </div>
              </div>
              <button className="ua-modal-close" onClick={closeActivity}><X size={20} /></button>
            </div>

            <div className="ua-tab-bar">
              <button className={`ua-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => switchTab('activity')}>
                <Zap size={15} /> Activity
              </button>
              <button className={`ua-tab ${activeTab === 'bookmarks' ? 'active' : ''}`} onClick={() => switchTab('bookmarks')}>
                <Bookmark size={15} /> Bookmarks
                {bookmarks.length > 0 && <span className="ua-tab-count">{bookmarks.length}</span>}
              </button>
              <button className={`ua-tab ${activeTab === 'cart' ? 'active' : ''}`} onClick={() => switchTab('cart')}>
                <ShoppingCart size={15} /> Cart
                {cart.length > 0 && <span className="ua-tab-count">{cart.length}</span>}
              </button>
              <button className={`ua-tab ${activeTab === 'product_views' ? 'active' : ''}`} onClick={() => switchTab('product_views')}>
                <ShoppingBag size={15} /> Product Views
                {productViews.length > 0 && <span className="ua-tab-count">{productViews.length}</span>}
              </button>
              <button className={`ua-tab ${activeTab === 'searches' ? 'active' : ''}`} onClick={() => switchTab('searches')}>
                <Search size={15} /> Searches
                {searches.length > 0 && <span className="ua-tab-count">{searches.length}</span>}
              </button>
              <button className={`ua-tab ${activeTab === 'barcodes' ? 'active' : ''}`} onClick={() => switchTab('barcodes')}>
                <ScanBarcode size={15} /> Barcode Scans
                {barcodeScans.length > 0 && <span className="ua-tab-count">{barcodeScans.length}</span>}
              </button>
              <button className={`ua-tab ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => switchTab('notifications')}>
                <Bell size={15} /> Notification
              </button>
            </div>

            <div className="ua-modal-body">
              {activeTab === 'activity' && (
                <>
                  {tlLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading timeline…</div>
                  ) : !timeline || timeline.sessions.length === 0 ? (
                    <div className="ua-empty">No activity data found.</div>
                  ) : (
                    <>
                      <div className="ua-modal-stats">
                        <div className="ua-mini-stat"><Layers size={16} /><div><span className="ua-mini-val">{timeline.summary.total_sessions}</span><span className="ua-mini-label">Sessions</span></div></div>
                        <div className="ua-mini-stat"><Zap size={16} /><div><span className="ua-mini-val">{timeline.summary.total_events}</span><span className="ua-mini-label">Events</span></div></div>
                        <div className="ua-mini-stat"><Smartphone size={16} /><div><span className="ua-mini-val">{timeline.summary.page_breakdown.length}</span><span className="ua-mini-label">Unique Pages</span></div></div>
                      </div>
                      <div className="ua-modal-section">
                        <h4>Page Visit Distribution</h4>
                        <div className="ua-chart-container">
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={timeline.summary.page_breakdown.map(p => ({ name: pageLabel(p.page_name), visits: p.visit_count, key: p.page_name }))}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip formatter={(val: number) => [val, 'Visits']} />
                              <Legend />
                              <Bar dataKey="visits" name="Visits" radius={[4, 4, 0, 0]}>
                                {timeline.summary.page_breakdown.map((p, i) => (
                                  <Cell key={i} fill={pageClr(p.page_name)} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div className="ua-modal-section">
                        <h4>Session Timeline (Most Recent First)</h4>
                        <div className="ua-sessions-list">
                          {[...timeline.sessions].reverse().map((session, si) => (
                            <UsersSessionTimeline key={session.session_id} session={session} index={si} />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === 'bookmarks' && (
                <>
                  {bookmarksLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading bookmarks…</div>
                  ) : bookmarks.length === 0 ? (
                    <div className="ua-empty"><Bookmark size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p>No bookmarks found.</p></div>
                  ) : (
                    <div className="ua-list-grid">
                      {bookmarks.map(b => (
                        <div key={b.id} className="ua-list-card">
                          <div className="ua-list-card-img">
                            {b.avater ? <img src={b.avater} alt={b.name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <Package size={28} />}
                          </div>
                          <div className="ua-list-card-info">
                            <span className="ua-list-card-name">{b.name}</span>
                            <div className="ua-list-card-meta">
                              {b.lowest_price > 0 && <span className="ua-list-card-price">${b.lowest_price.toFixed(2)}</span>}
                              <span className="ua-list-card-detail"><Heart size={12} /> {b.bookmark_count}</span>
                              {b.is_feature && <span className="ua-list-card-badge">Featured</span>}
                            </div>
                            {b.created_at && <span className="ua-list-card-date">Bookmarked {fmtDate(b.created_at)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'product_views' && (
                <>
                  {productViewsLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading product views…</div>
                  ) : productViews.length === 0 ? (
                    <div className="ua-empty"><ShoppingBag size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p>No product views found.</p></div>
                  ) : (() => {
                    const grouped: Record<string, typeof productViews> = {};
                    for (const v of productViews) {
                      const dayKey = v.viewed_at ? fmtDate(v.viewed_at) : 'Unknown date';
                      (grouped[dayKey] ??= []).push(v);
                    }
                    const toggleDay = (day: string) => {
                      setCollapsedDays(prev => {
                        const next = new Set(prev);
                        next.has(day) ? next.delete(day) : next.add(day);
                        return next;
                      });
                    };
                    return (
                      <div className="ua-product-views-list">
                        {Object.entries(grouped).map(([day, views]) => (
                          <div key={day} className="ua-product-views-day-group">
                            <div className="ua-day-header ua-day-header-clickable" onClick={() => toggleDay(day)}>
                              <ChevronDown size={14} className={`ua-day-chevron ${collapsedDays.has(day) ? 'collapsed' : ''}`} />
                              <Clock size={14} />
                              <span>{day}</span>
                              <span className="ua-day-count">{views.length} product{views.length !== 1 ? 's' : ''}</span>
                            </div>
                            {!collapsedDays.has(day) && <div className="ua-product-views-grid">
                              {views.map(v => (
                                <div key={v.id} className="ua-product-view-card">
                                  <div className="ua-product-view-img">
                                    {v.product_image ? <img src={v.product_image} alt={v.product_name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <Package size={28} />}
                                  </div>
                                  <div className="ua-product-view-info">
                                    <span className="ua-product-view-name">{v.product_name}</span>
                                    <div className="ua-product-view-meta">
                                      <span className="ua-product-view-count"><Eye size={12} /> {v.view_count} view{v.view_count !== 1 ? 's' : ''}</span>
                                      {v.device && <span className="ua-product-view-device"><Smartphone size={12} /> {v.device}</span>}
                                      {v.os && <span className="ua-product-view-os"><Monitor size={12} /> {v.os}</span>}
                                    </div>
                                    {v.viewed_at && <span className="ua-product-view-date">{fmtTime(v.viewed_at)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}

              {activeTab === 'searches' && (
                <>
                  {searchesLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading searches…</div>
                  ) : searches.length === 0 ? (
                    <div className="ua-empty"><Search size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p>No searches found.</p></div>
                  ) : (
                    <div className="ua-search-list">
                      <table className="ua-table ua-search-table">
                        <thead><tr><th>Search Keyword</th><th>Results Found</th><th>Country</th><th>Searched At</th></tr></thead>
                        <tbody>
                          {searches.map(s => (
                            <tr key={s.id}>
                              <td><span className="ua-search-keyword"><Search size={14} /> {s.keyword}</span></td>
                              <td><span className={`ua-search-results ${s.results_count === 0 ? 'zero' : ''}`}>{s.results_count}</span></td>
                              <td>{s.country || '—'}</td>
                              <td className="ua-last-active">{s.searched_at ? fmtDate(s.searched_at) + ' ' + fmtTime(s.searched_at) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'barcodes' && (
                <>
                  {barcodesLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading barcode scans…</div>
                  ) : barcodeScans.length === 0 ? (
                    <div className="ua-empty"><ScanBarcode size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p>No barcode scans found.</p></div>
                  ) : (
                    <div className="ua-barcode-list">
                      <table className="ua-table ua-barcode-table">
                        <thead><tr><th>Barcode</th><th>Results Found</th><th>Country</th><th>Scanned At</th></tr></thead>
                        <tbody>
                          {barcodeScans.map(s => (
                            <tr key={s.id}>
                              <td><span className="ua-barcode-value"><ScanBarcode size={14} /> {s.barcode}</span></td>
                              <td><span className={`ua-barcode-results ${s.results_count === 0 ? 'zero' : ''}`}>{s.results_count}</span></td>
                              <td>{s.country || '—'}</td>
                              <td className="ua-last-active">{s.scanned_at ? fmtDate(s.scanned_at) + ' ' + fmtTime(s.scanned_at) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'cart' && (
                <>
                  {cartLoading ? (
                    <div className="ua-loading"><RefreshCw size={24} /> Loading cart…</div>
                  ) : cart.length === 0 ? (
                    <div className="ua-empty"><ShoppingCart size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p>No items in cart.</p></div>
                  ) : (
                    <div className="ua-cart-list">
                      <div className="ua-cart-summary">
                        <span>{cart.length} item{cart.length !== 1 ? 's' : ''}</span>
                        <span className="ua-cart-total">Total: ${cart.reduce((sum, c) => sum + c.price * c.quantity, 0).toFixed(2)}</span>
                      </div>
                      {cart.map(c => (
                        <div key={c.shopping_id} className="ua-cart-item">
                          <div className="ua-cart-item-img">
                            {c.avater ? <img src={c.avater} alt={c.item_name} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <Package size={24} />}
                          </div>
                          <div className="ua-cart-item-info">
                            <span className="ua-cart-item-name">{c.itemkit_name || c.item_name}</span>
                            <span className="ua-cart-item-variant">{c.item_name}</span>
                            <div className="ua-cart-item-meta">
                              {c.retailer?.name && <span className="ua-cart-item-retailer"><Store size={12} /> {c.retailer.name}</span>}
                              {c.added_by_user_name && <span className="ua-cart-item-addedby">Added by {c.added_by_user_name}</span>}
                              {c.created_at && <span className="ua-cart-item-date">{fmtDate(c.created_at)}</span>}
                            </div>
                          </div>
                          <div className="ua-cart-item-right">
                            <span className="ua-cart-item-qty">×{c.quantity}</span>
                            <span className="ua-cart-item-price">${c.price.toFixed(2)}</span>
                            {!c.is_available && <span className="ua-cart-item-unavailable">Unavailable</span>}
                            {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="ua-cart-item-link"><ExternalLink size={12} /></a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'notifications' && (
                <div className="ua-tab-content">
                  {notifLoading ? (
                    <div className="ua-loading"><RefreshCw size={20} className="spin" /> Checking notification status…</div>
                  ) : notifEnabled === false ? (
                    <div className="notif-disabled">
                      <Bell size={32} style={{ opacity: 0.3 }} />
                      <p>Notifications are disabled for this user.</p>
                      <span>This user has no active devices registered for push notifications.</span>
                    </div>
                  ) : notifEnabled ? (
                    <div className="notif-form">
                      {notifResult === 'success' && (
                        <div className="notif-result success">Notification sent successfully!</div>
                      )}
                      {notifResult === 'error' && (
                        <div className="notif-result error">Failed to send notification. Please try again.</div>
                      )}
                      <label className="notif-label">Title</label>
                      <input
                        className="notif-input"
                        type="text"
                        placeholder="Notification title"
                        value={notifTitle}
                        onChange={e => setNotifTitle(e.target.value)}
                        maxLength={100}
                      />
                      <label className="notif-label">Message</label>
                      <textarea
                        className="notif-textarea"
                        placeholder="Notification message"
                        value={notifBody}
                        onChange={e => setNotifBody(e.target.value)}
                        rows={4}
                        maxLength={500}
                      />
                      <button
                        className="notif-send-btn"
                        onClick={handleSendNotification}
                        disabled={notifSending || !notifTitle.trim() || !notifBody.trim()}
                      >
                        {notifSending ? <><RefreshCw size={16} className="spin" /> Sending…</> : <><Send size={16} /> Send Notification</>}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Helpers ── */
const PAGE_LABELS: Record<string, string> = {
  home: 'Home', search: 'Search', product_detail: 'Product Details', posts: 'Posts',
  shopping_cart: 'Shopping Cart', bookmarks: 'Bookmarks', app_open: 'App Open', app_close: 'App Close',
};
const pageLabel = (p: string) => PAGE_LABELS[p] || p;

const PAGE_COLORS: Record<string, string> = {
  home: '#6366f1', search: '#8b5cf6', product_detail: '#ec4899', posts: '#f59e0b',
  shopping_cart: '#10b981', bookmarks: '#06b6d4', app_open: '#94a3b8', app_close: '#94a3b8',
};
const pageClr = (p: string) => PAGE_COLORS[p] || '#6b7280';

const fmtDuration = (s: number) => {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const speedClass = (ms: number) => (ms <= 500 ? 'speed-fast' : ms <= 1500 ? 'speed-ok' : 'speed-slow');

/* ── Session Timeline Sub-Component ── */
const UsersSessionTimeline: React.FC<{ session: TimelineSession; index: number }> = ({ session, index }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const pageEvents = session.events.filter(e => e.page_name !== 'app_open' && e.page_name !== 'app_close');

  return (
    <div className="ua-session-block">
      <button className="ua-session-header" onClick={() => setExpanded(!expanded)}>
        <div className="ua-session-header-left">
          <ChevronRightIcon size={16} className={`ua-chevron ${expanded ? 'open' : ''}`} />
          <Clock size={14} />
          <span className="ua-session-date">{fmtDate(session.start)}</span>
          <span className="ua-session-time">{fmtTime(session.start)} — {fmtTime(session.end)}</span>
        </div>
        <div className="ua-session-header-right">
          <span className="ua-session-meta">{pageEvents.length} pages</span>
          <span className="ua-session-meta">{fmtDuration(session.duration_seconds)}</span>
        </div>
      </button>
      {expanded && (
        <div className="ua-session-events">
          <div className="ua-session-chart">
            <ResponsiveContainer width="100%" height={Math.max(120, pageEvents.length * 36 + 40)}>
              <BarChart data={pageEvents.map(e => ({ name: pageLabel(e.page_name), duration: e.time_on_page_seconds, key: e.page_name }))} layout="vertical" margin={{ left: 100, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} unit="s" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={90} />
                <Tooltip formatter={(val: number) => [`${val}s`, 'Time on Page']} />
                <Bar dataKey="duration" name="Time on Page (s)" radius={[0, 4, 4, 0]}>
                  {pageEvents.map((e, i) => <Cell key={i} fill={pageClr(e.page_name)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="ua-event-list">
            {session.events.map((e, i) => (
              <div key={i} className={`ua-event ${e.page_name === 'app_open' || e.page_name === 'app_close' ? 'marker' : ''}`}>
                <div className="ua-event-dot" style={{ background: pageClr(e.page_name) }} />
                <div className="ua-event-time">{fmtTime(e.timestamp)}</div>
                <div className="ua-event-page">{pageLabel(e.page_name)}</div>
                {e.page_name !== 'app_open' && e.page_name !== 'app_close' && (
                  <>
                    <span className={`ua-event-load ${speedClass(e.load_time_ms)}`}>{e.load_time_ms}ms</span>
                    {e.time_on_page_seconds > 0 && <span className="ua-event-duration">{fmtDuration(e.time_on_page_seconds)}</span>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
